import { SYSTEM_FONTS } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import { collectStoryStyleAssignments } from 'common/design-tokens/story-style-assignments';
import { collectEffectiveFontFaces, hasPresetTypography } from 'common/design-tokens/preset-fonts';
import {
	canonicalFontFamily,
	createFontResolver,
	type ResolvedFont,
} from 'admin/components/common/AI/Chat/fonts/font-resolver';
import { validateDesignPresetFontFace } from 'admin/components/common/DesignSystemEditor/design-preset-binding';
import { registerResolvedFonts } from 'admin/components/pages/Story/StoryEditorChat/tools/update-project/register-resolved-fonts';
import type { IOrganizationFonts } from 'types/organization';
import type { StorySettingsType, StoryVersionType } from 'types/story';

type StoryFontInput = { data: StoryVersionType['data']; settings: StorySettingsType };

/** System-font loading follows actual assignments and every mode, without validating custom faces. */
export function collectStorySystemFonts({
	data,
	settings,
}: StoryFontInput): NonNullable<StorySettingsType['systemFonts']> {
	const systemFonts = new Map(Object.values(SYSTEM_FONTS).map(font => [canonicalFontFamily(font.fontFamily), font]));
	const used = new Map<string, NonNullable<StorySettingsType['systemFonts']>[number]>();
	for (const { assignments } of collectStoryStyleAssignments({ data, design: settings.design })) {
		for (const { family } of collectEffectiveFontFaces({
			design: settings.design,
			mediaQuery: data.mediaQuery,
			styles: assignments.styles,
		})) {
			const font = systemFonts.get(canonicalFontFamily(family));
			if (font) used.set(font.url, { url: font.url, fontType: font.fontType });
		}
	}
	return [...used.values()];
}

/** Prepare only faces used by linked typography; failure occurs before the publication request. */
export function preparePublishedPresetFonts({
	data,
	settings,
	organizationFonts,
}: StoryFontInput & { organizationFonts?: IOrganizationFonts }): StorySettingsType {
	const resolver = createFontResolver({ storyFonts: settings.fonts, organizationFonts, googleFonts: [] });
	const imports = new Map<string, ResolvedFont>();
	for (const { element, assignments } of collectStoryStyleAssignments({ data, design: settings.design })) {
		if (!element.uiConfig.componentProps[UNIFORM_PROPS.designPresetId] || !hasPresetTypography(assignments))
			continue;
		const invalid = assignments.diagnostics.find(
			diagnostic =>
				(diagnostic.property === 'fontFamily' || diagnostic.property === 'fontWeight') &&
				(diagnostic.code === 'missing-token' || diagnostic.code === 'incompatible-token') &&
				assignments.sources.defaultState?.[diagnostic.platform ?? '']?.[diagnostic.property]?.kind === 'preset'
		);
		if (invalid)
			throw new Error(`Block "${element._id}" has an unresolved ${invalid.property} token "${invalid.tokenId}".`);
		for (const required of collectEffectiveFontFaces({
			design: settings.design,
			mediaQuery: data.mediaQuery,
			styles: assignments.styles,
		})) {
			const font = validateDesignPresetFontFace(
				resolver.resolveExact(required.family),
				required.family,
				required.weight
			);
			if (font.source === 'organization') imports.set(canonicalFontFamily(required.family), font);
		}
	}
	return imports.size
		? { ...settings, fonts: registerResolvedFonts(settings.fonts ?? {}, [...imports.values()]) }
		: settings;
}
